import _ from "lodash";
import zod from "zod";

import { UserWithCompany } from "@/utils/types";
import { LogicProposalComponent } from "lib/frontend/cm/utils/types";
import { isCompanyAdmin } from "lib/shared/permissions";
import {
  getProposalDbFieldToAutofill,
  getProposalEquityValues,
  hasBonusBeenManuallyEdited,
  hasSalaryBeenManuallyEdited,
  hasBonusTargetBeenManuallyEdited,
} from "lib/shared/proposals/index";
import { Review, ReviewStatus, SalaryIncreaseType } from "prisma/cm/client";

import { ReviewExchangeRatesSchema } from "./types";
import { Proposal } from "../proposals/types";

export function getIsOTEReview(review: Review) {
  return review.salaryIncreaseType === SalaryIncreaseType.TOTAL_PAY;
}

/*
 * This looks silly but it's type–safe - if a status ever gets added,
 * TypeScript will let us know if it's not considered here.
 */
const reviewStatusToStartedResult: Record<Review["status"], boolean> = {
  [ReviewStatus.SETUP]: false,
  [ReviewStatus.IN_PROGRESS]: true,
  [ReviewStatus.ENDED]: true,
};
/*
 * This looks silly but it's type–safe - if a status ever gets added,
 * TypeScript will let us know if it's not considered here.
 */
const reviewStatusToEndedResult: Record<Review["status"], boolean> = {
  [ReviewStatus.SETUP]: false,
  [ReviewStatus.IN_PROGRESS]: false,
  [ReviewStatus.ENDED]: true,
};
export function getReviewHasStarted(review: Review) {
  return reviewStatusToStartedResult[review.status];
}
export function getReviewHasEnded(review: Review) {
  return reviewStatusToEndedResult[review.status];
}

export const autoFillSettingsSchema = zod.object({
  raise: zod.boolean(),
  bonus: zod.boolean(),
  bonusTarget: zod.boolean().optional(),
  equity: zod.boolean(),
  raiseMutable: zod.boolean(),
  bonusMutable: zod.boolean(),
  bonusTargetMutable: zod.boolean().optional(),
  equityMutable: zod.boolean(),
  promotionsMutable: zod.boolean(),
});

export function getReviewAutofillSettings(review: Review) {
  return autoFillSettingsSchema.parse(review.autoFillSettings);
}

export type AutoFillSettings = zod.infer<typeof autoFillSettingsSchema>;

export function getReviewExchangeRates(review: Review) {
  return ReviewExchangeRatesSchema.parse(review.exchangeRates);
}

export function shouldAutofillActualValue({
  review,
  proposal,
  logicComponent,
}: {
  review: Review;
  proposal: Proposal;
  logicComponent: LogicProposalComponent;
}) {
  const autoFillSettings = review.autoFillSettings;
  const proposalDbFieldToAutofill = getProposalDbFieldToAutofill(logicComponent);

  const autofillEnabledForThisReviewComponent =
    _.get(autoFillSettings, proposalDbFieldToAutofill) === true;

  const shouldPreventAutofill = (() => {
    if (logicComponent === LogicProposalComponent.SALARY) {
      return hasSalaryBeenManuallyEdited(proposal);
    } else if (logicComponent === LogicProposalComponent.BONUS) {
      return hasBonusBeenManuallyEdited(proposal);
    } else if (logicComponent === LogicProposalComponent.BONUS_TARGET) {
      return hasBonusTargetBeenManuallyEdited(proposal);
    } else {
      // throw new Error(`Unknown logic component: ${logicComponent}`);
      return true;
    }
  })();

  const shouldAutofillThisProposalComponent =
    autofillEnabledForThisReviewComponent && !shouldPreventAutofill;

  return shouldAutofillThisProposalComponent;
}

export function shouldAutofillActualValueEquity<T extends Proposal>({
  review,
  proposal,
  grantTypeId,
}: {
  review: Review;
  proposal: T;
  grantTypeId: string;
}) {
  const autoFillSettings = review.autoFillSettings;
  const autofillEnabledForThisReviewComponent = _.get(autoFillSettings, "equity") === true;

  const shouldPreventAllEquityAutofill = getProposalEquityValues(
    proposal,
    grantTypeId,
  )?.isManuallyEdited;

  const shouldAttemptAutofillEquity = autofillEnabledForThisReviewComponent;
  const shouldAutofillEquity = shouldAttemptAutofillEquity && !shouldPreventAllEquityAutofill;

  return shouldAutofillEquity;
}

export function hasEditAccessForComponent({
  user,
  review,
  reviewComponent,
  forceReadonly = false,
}: {
  review: Review;
  user: UserWithCompany;
  reviewComponent: keyof AutoFillSettings;
  forceReadonly?: boolean;
}) {
  const isAdmin = isCompanyAdmin(user);

  if (isAdmin) {
    return true;
  }

  // @ts-expect-error TS7053
  const readOnly = review.autoFillSettings?.[reviewComponent] === false;

  return !readOnly && !forceReadonly;
}

export function suggestionsEnabled(
  review: Pick<Review, "enabledSuggestionComponents">,
  component: LogicProposalComponent,
) {
  return review.enabledSuggestionComponents.includes(component);
}
